import React from "react";
import secondsToTimecode from "utils/secondsToTimecode";
import { Subtitle } from "core/models";
import styles from "./SubtitleTimeline.module.scss";

interface Props {
    videoRef: React.MutableRefObject<HTMLVideoElement>;
    subtitles: Subtitle[];
    activeSubtitle?: Subtitle;
    onSubtitleClick(newActiveSubtitle: Subtitle): void;
    onSubtitleHover(hoveredSubtitle: Subtitle | undefined): void;
}
const SubtitleTimeline: React.FC<Props> = ({
    videoRef,
    activeSubtitle,
    subtitles,
    onSubtitleClick,
    onSubtitleHover,
}) => {
    const trackRef = React.useRef<HTMLDivElement>(null);
    const subtitleWidth = React.useRef<number>(0);

    const activeSubtitleStyle = (subtitle: Subtitle): React.CSSProperties => {
        const trackWidth = trackRef.current?.offsetWidth ?? 0;
        if (!videoRef.current || !trackWidth || !subtitle) {
            return {};
        }
        const inPos = Math.ceil(
            trackWidth * (subtitle.startTime / videoRef.current.duration),
        );
        let outPos = Math.ceil(
            trackWidth * (subtitle.endTime / videoRef.current.duration),
        );
        outPos = outPos > trackWidth ? trackWidth - inPos : outPos - inPos;
        subtitleWidth.current = outPos;
        return {
            left: `${inPos}px`,
            width: `${outPos}px`,
        };
    };

    return (
        <div className={styles.container}>
            <div className={styles.subtitles} ref={trackRef}>
                {activeSubtitle && (
                    <div
                        className={styles.subtitles__active}
                        style={activeSubtitleStyle(activeSubtitle)}
                    >
                        <div>{secondsToTimecode(activeSubtitle.startTime)}</div>
                        {subtitleWidth.current > 80 && (
                            <div>
                                {secondsToTimecode(activeSubtitle.endTime)}
                            </div>
                        )}
                    </div>
                )}
                {subtitles.map((subtitle) => {
                    if (subtitle === activeSubtitle) {
                        return null;
                    }
                    return (
                        <div
                            className={styles.subtitles__item}
                            style={activeSubtitleStyle(subtitle)}
                            key={subtitle.id}
                            role="presentation"
                            onMouseEnter={() => onSubtitleHover(subtitle)}
                            onMouseLeave={() => onSubtitleHover(undefined)}
                            onClick={() => onSubtitleClick(subtitle)}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default SubtitleTimeline;
